import { useState } from 'react';
import { useNavigate, useRevalidator } from 'react-router';
import { Button } from '@/components/ui/button';
import { api, errMsg } from '@/lib/api';

export function StyleForm({ initial }: { initial?: { id: string; name: string; description: string | null; guidelines: string | null; system: boolean } }) {
  const navigate = useNavigate();
  const { revalidate } = useRevalidator();
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [guidelines, setGuidelines] = useState(initial?.guidelines ?? '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  async function save() {
    setBusy(true);
    setMsg(null);
    try {
      const body = { name, description: description || undefined, guidelines: guidelines || undefined };
      if (initial) {
        await api(`/styles/${initial.id}`, { method: 'PATCH', json: body });
        setMsg({ kind: 'ok', text: 'Saved' });
        revalidate();
      } else {
        const s = await api<{ id: string }>('/styles', { method: 'POST', json: body });
        navigate(`/templates/styles/${s.id}`);
      }
    } catch (err) {
      setMsg({ kind: 'error', text: errMsg(err, 'Save failed') });
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!initial || !confirm(`Delete style "${initial.name}"? Templates keep their content and just lose the style tag.`)) return;
    setBusy(true);
    try {
      await api(`/styles/${initial.id}`, { method: 'DELETE' });
      navigate('/templates/styles');
      revalidate();
    } catch (err) {
      setMsg({ kind: 'error', text: errMsg(err, 'Delete failed') });
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3 rounded-lg border bg-white p-4 text-sm">
      <h2 className="text-base">{initial ? 'Edit style' : 'New style'}</h2>
      <label className="block space-y-1">
        <span className="text-xs text-muted-foreground">Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} className="h-9 w-full rounded-md border px-2" placeholder="e.g. Case study" />
      </label>
      <label className="block space-y-1">
        <span className="text-xs text-muted-foreground">One-line description (shown in pickers)</span>
        <input value={description} onChange={(e) => setDescription(e.target.value)} className="h-9 w-full rounded-md border px-2" />
      </label>
      <label className="block space-y-1">
        <span className="text-xs text-muted-foreground">Guidelines for the AI — structure, tone, sections, do/don't</span>
        <textarea value={guidelines} onChange={(e) => setGuidelines(e.target.value)} rows={12} className="w-full rounded-md border p-2 text-xs" placeholder={'Structure: …\nTone: …\nDesign: …'} />
      </label>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={save} disabled={busy || !name.trim()}>
          {initial ? 'Save' : 'Create style'}
        </Button>
        {initial && !initial.system && (
          <Button size="sm" variant="outline" onClick={remove} disabled={busy}>
            Delete
          </Button>
        )}
        {msg && <span className={`text-xs ${msg.kind === 'ok' ? 'text-emerald-700' : 'text-red-700'}`}>{msg.text}</span>}
      </div>
    </div>
  );
}
